import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import { resolveEquipmentReference, ResolvedEquipmentItem } from '../../utils/equipmentResolver';
import { showToast } from '../../utils/toast';
import { itemLoader } from '../../utils/itemLoader';
import { LootItem, ConsumableItem, isConsumableItem } from '../../types/equipment';
import { MAX_CONSUMABLE_HOLD } from '../../constants/equipment';
import Input from '../Common/Input';
import Button from '../Common/Button';
import './Inventory.css';

interface InventoryProps {
  enhancedCharacter: EnhancedCharacter;
}

const Inventory: React.FC<InventoryProps> = ({ enhancedCharacter }) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [expandedReferences, setExpandedReferences] = useState<Set<string>>(new Set());
  const [resolvedEquipment, setResolvedEquipment] = useState<Map<string, ResolvedEquipmentItem>>(new Map());
  const [loadingEquipment, setLoadingEquipment] = useState<Set<string>>(new Set());
  const [resolvedLoot, setResolvedLoot] = useState<Map<string, LootItem>>(new Map());
  const [loadingLoot, setLoadingLoot] = useState<Set<string>>(new Set());
  const [resolvedConsumables, setResolvedConsumables] = useState<Map<string, ConsumableItem>>(new Map());
  const [loadingConsumables, setLoadingConsumables] = useState<Set<string>>(new Set());
  const [itemLoaderInitialized, setItemLoaderInitialized] = useState(false);

  const character = enhancedCharacter.character;
  const inventory = character?.inventory?.manualItems || [];
  const equipmentReferences = character?.inventory?.equipmentReferences || [];
  const lootReferences = character?.inventory?.lootReferences || [];
  const consumableReferences = character?.inventory?.consumableReferences || [];

  const toggleReferenceExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedReferences);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedReferences(newExpanded);
  };

  // Initialize itemLoader
  useEffect(() => {
    const init = async () => {
      try {
        await itemLoader.initialize();
        setItemLoaderInitialized(true);
      } catch (err) {
        console.error('Failed to initialize item loader:', err);
      }
    };
    if (!itemLoaderInitialized) {
      init();
    }
  }, [itemLoaderInitialized]);

  // Effect to resolve equipment references when they change
  useEffect(() => {
    const resolveEquipmentData = async () => {
      const newResolved = new Map<string, ResolvedEquipmentItem>();
      const newLoading = new Set<string>();

      for (const ref of equipmentReferences) {
        if (!resolvedEquipment.has(ref.itemId)) {
          newLoading.add(ref.itemId);
        }
      }

      setLoadingEquipment(newLoading);

      // Resolve each equipment reference
      for (const ref of equipmentReferences) {
        if (!resolvedEquipment.has(ref.itemId)) {
          try {
            const resolved = await resolveEquipmentReference(ref.itemId);
            if (resolved) {
              newResolved.set(ref.itemId, resolved);
            }
          } catch (error) {
            console.error(`Failed to resolve equipment reference ${ref.itemId}:`, error);
          }
        }
      }

      // Update resolved equipment map
      if (newResolved.size > 0) {
        setResolvedEquipment(prev => {
          const updated = new Map(prev);
          newResolved.forEach((value, key) => {
            updated.set(key, value);
          });
          return updated;
        });
      }

      // Clear loading state
      setLoadingEquipment(new Set());
    };

    if (equipmentReferences.length > 0) {
      resolveEquipmentData();
    }
  }, [equipmentReferences, resolvedEquipment]);

  // Effect to resolve loot items when they change
  useEffect(() => {
    if (!itemLoaderInitialized) return;

    const newResolved = new Map<string, LootItem>();
    const newLoading = new Set<string>();

    for (const ref of lootReferences) {
      if (!resolvedLoot.has(ref.itemId)) {
        newLoading.add(ref.itemId);
        const item = itemLoader.findItemById(ref.itemId);
        if (item && !isConsumableItem(item)) {
          newResolved.set(ref.itemId, item as LootItem);
        }
      }
    }

    setLoadingLoot(newLoading);

    if (newResolved.size > 0) {
      setResolvedLoot(prev => {
        const updated = new Map(prev);
        newResolved.forEach((value, key) => {
          updated.set(key, value);
        });
        return updated;
      });
    }

    setLoadingLoot(new Set());
  }, [lootReferences, resolvedLoot, itemLoaderInitialized]);

  // Effect to resolve consumable items when they change
  useEffect(() => {
    if (!itemLoaderInitialized) return;

    const newResolved = new Map<string, ConsumableItem>();
    const newLoading = new Set<string>();

    for (const ref of consumableReferences) {
      if (!resolvedConsumables.has(ref.itemId)) {
        newLoading.add(ref.itemId);
        const item = itemLoader.findItemById(ref.itemId);
        if (item && isConsumableItem(item)) {
          newResolved.set(ref.itemId, item as ConsumableItem);
        }
      }
    }

    setLoadingConsumables(newLoading);

    if (newResolved.size > 0) {
      setResolvedConsumables(prev => {
        const updated = new Map(prev);
        newResolved.forEach((value, key) => {
          updated.set(key, value);
        });
        return updated;
      });
    }

    setLoadingConsumables(new Set());
  }, [consumableReferences, resolvedConsumables, itemLoaderInitialized]);

  // Equipment reference handlers
  const handleReferenceQuantityChange = (itemId: string, newQuantity: number) => {
    if (enhancedCharacter.addEquipmentByReference && enhancedCharacter.removeEquipmentByReference) {
      const currentRef = equipmentReferences.find(ref => ref.itemId === itemId);
      if (currentRef) {
        const difference = newQuantity - currentRef.quantity;
        if (difference > 0) {
          enhancedCharacter.addEquipmentByReference(itemId, difference);
        } else if (difference < 0) {
          enhancedCharacter.removeEquipmentByReference(itemId, Math.abs(difference));
        }
      }
    }
  };

  const handleReferenceEquipToggle = (itemId: string, currentEquipped: number, maxQuantity: number) => {
    if (enhancedCharacter.equipItemByReference && enhancedCharacter.unequipItemByReference) {
      if (currentEquipped > 0) {
        // Unequip all
        enhancedCharacter.unequipItemByReference(itemId, currentEquipped);
      } else {
        // Equip one (or all if quantity is 1)
        const equipCount = Math.min(1, maxQuantity);
        enhancedCharacter.equipItemByReference(itemId, equipCount);
      }
    }
  };

  const handleRemoveReference = (itemId: string) => {
    if (enhancedCharacter.removeEquipmentByReference) {
      // Remove all quantity
      enhancedCharacter.removeEquipmentByReference(itemId, 999);
    }
  };

  // Loot item handlers
  const handleLootQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    if (enhancedCharacter.updateLootQuantity) {
      enhancedCharacter.updateLootQuantity(itemId, newQuantity);
    }
  };

  const handleLootEquipToggle = (itemId: string) => {
    if (!enhancedCharacter.equipItemByReference || !enhancedCharacter.unequipItemByReference || !enhancedCharacter.addEquipmentByReference) return;

    // Check if item exists in equipmentReferences and get equipped count
    const equipmentRef = equipmentReferences.find(ref => ref.itemId === itemId);
    const currentEquipped = equipmentRef?.equippedCount || 0;

    if (currentEquipped > 0) {
      // Unequip all
      enhancedCharacter.unequipItemByReference(itemId, currentEquipped);
    } else {
      // Item not in equipmentReferences yet, add it first
      if (!equipmentRef) {
        enhancedCharacter.addEquipmentByReference(itemId, 1);
      }
      // Equip one
      enhancedCharacter.equipItemByReference(itemId, 1);
    }
  };

  const handleRemoveLoot = (itemId: string) => {
    if (enhancedCharacter.removeLootByReference) {
      enhancedCharacter.removeLootByReference(itemId);

      // Also remove from equipmentReferences if it was equipped
      const equipmentRef = equipmentReferences.find(ref => ref.itemId === itemId);
      if (equipmentRef && enhancedCharacter.removeEquipmentByReference) {
        enhancedCharacter.removeEquipmentByReference(itemId, 999); // Remove all quantity
      }

      showToast.success('Removed loot item from inventory');
    }
  };

  // Consumable item handlers
  const handleConsumableQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    if (enhancedCharacter.updateConsumableQuantity) {
      enhancedCharacter.updateConsumableQuantity(itemId, newQuantity);
    }
  };

  const handleConsume = (itemId: string) => {
    const currentRef = consumableReferences.find(ref => ref.itemId === itemId);
    if (!currentRef || currentRef.quantity <= 0) return;

    const newQuantity = currentRef.quantity - 1;
    if (newQuantity === 0) {
      handleRemoveConsumable(itemId);
    } else {
      handleConsumableQuantityChange(itemId, newQuantity);
    }
  };

  const handleRemoveConsumable = (itemId: string) => {
    if (enhancedCharacter.removeConsumableByReference) {
      enhancedCharacter.removeConsumableByReference(itemId);

      // Also remove from equipmentReferences if it was equipped
      const equipmentRef = equipmentReferences.find(ref => ref.itemId === itemId);
      if (equipmentRef && enhancedCharacter.removeEquipmentByReference) {
        enhancedCharacter.removeEquipmentByReference(itemId, 999); // Remove all quantity
      }

      showToast.success('Removed consumable item from inventory');
    }
  };

  if (!enhancedCharacter.character) {
    return (
      <motion.div 
        className="inventory"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="inventory__loading">
          Loading inventory...
        </div>
      </motion.div>
    );
  }

  const toggleItemExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const formatEquipmentType = (type: string): string => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatPropertyValue = (value: string | number): string => {
    if (typeof value === 'string') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    return String(value);
  };


  const handleItemChange = (index: number, field: 'name' | 'description' | 'quantity') => (value: string) => {
    const updatedInventory = [...inventory];
    if (field === 'quantity') {
      updatedInventory[index] = {
        ...updatedInventory[index],
        [field]: parseInt(value) || 0,
      };
    } else {
      updatedInventory[index] = {
        ...updatedInventory[index],
        [field]: value,
      };
    }
    
    if (enhancedCharacter.updateInventoryManualItems) {
      enhancedCharacter.updateInventoryManualItems(updatedInventory);
    }
  };

  const addItem = () => {
    const updatedInventory = [...inventory, { name: '', description: '', quantity: 1 }];
    
    if (enhancedCharacter.updateInventoryManualItems) {
      enhancedCharacter.updateInventoryManualItems(updatedInventory);
      showToast.success('New item added to inventory');
    }
  };

  const removeItem = (index: number) => {
    const itemName = inventory[index]?.name || 'Item';
    const updatedInventory = inventory.filter((_, i) => i !== index);

    if (enhancedCharacter.updateInventoryManualItems) {
      enhancedCharacter.updateInventoryManualItems(updatedInventory);
      showToast.success(`Removed ${itemName} from inventory`);
    }
  };

  // Filter out loot and consumables from Equipment section - they have their own sections
  const equipmentOnlyReferences = equipmentReferences.filter(ref => {
    const resolved = resolvedEquipment.get(ref.itemId);
    return !resolved || (resolved.type !== 'loot' && resolved.type !== 'consumable');
  });

  return (
    <motion.div 
      className="inventory"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Equipment References Section */}
      <AnimatePresence>
        {equipmentOnlyReferences.length > 0 && (
          <motion.div 
            key="equipment-section"
            className="inventory__section"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <motion.h3 
              className="inventory__section-title"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              Equipment
            </motion.h3>
            <motion.div
              className="inventory__equipment-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
            <AnimatePresence>
              {equipmentOnlyReferences.map((ref, index) => {
                const resolved = resolvedEquipment.get(ref.itemId);
                const isExpanded = expandedReferences.has(ref.itemId);
                const isLoading = loadingEquipment.has(ref.itemId);
                
                return (
                  <motion.div
                    key={ref.itemId}
                    className="inventory__equipment-item"
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ delay: 0.05, duration: 0.2 }}
                  >
                    <motion.div 
                      className="inventory__equipment-header" 
                      onClick={() => toggleReferenceExpanded(ref.itemId)}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="inventory__equipment-title-row">
                        <motion.button 
                          className={`inventory__dropdown-arrow ${isExpanded ? 'inventory__dropdown-arrow--expanded' : ''}`}
                          animate={{ rotate: isExpanded ? 90 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          ▼
                        </motion.button>
                        <motion.span className="inventory__equipment-name">
                          {resolved
                            ? resolved.name.charAt(0).toUpperCase() + resolved.name.slice(1)
                            : (isLoading ? 'Loading...' : ref.itemId.charAt(0).toUpperCase() + ref.itemId.slice(1))}
                        </motion.span>
                        <div className="inventory__equipment-quantities">
                          <span className="inventory__equipment-quantity">
                            ×{ref.quantity}
                          </span>
                          {ref.equippedCount > 0 && (
                            <span className="inventory__equipment-equipped">
                              ✓ {ref.equippedCount} equipped
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  <AnimatePresence>
                    {isExpanded && resolved && (
                      <motion.div
                        className="inventory__equipment-details"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.35 }}
                      >
                      
                      {/* Equipment Properties - Match Content Library Style */}
                      <div className="inventory__equipment-properties">
                        {/* Basic Equipment Info - Always show */}
                        <div className="inventory__equipment-property">
                          <span className="inventory__equipment-property-label">Type:</span>
                          <span className="inventory__equipment-property-value">{formatEquipmentType(resolved.type)}</span>
                        </div>
                        
                        {/* Show comprehensive properties for equipment type */}
                        {resolved.type === 'weapon' && (
                          <>
                            {/* Weapon-specific properties */}
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Tier:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).tier || 'None'}</span>
                            </div>
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Trait:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).trait ? formatPropertyValue((resolved as any).trait) : 'None'}</span>
                            </div>
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Range:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).range ? formatPropertyValue((resolved as any).range) : 'None'}</span>
                            </div>
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Damage:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).damage || 'None'}</span>
                            </div>
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Damage Type:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).damageType ? formatPropertyValue((resolved as any).damageType) : 'None'}</span>
                            </div>
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Burden:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).burden ? formatPropertyValue((resolved as any).burden) : 'None'}</span>
                            </div>
                          </>
                        )}
                        
                        {resolved.type === 'armor' && (
                          <>
                            {/* Armor-specific properties */}
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Tier:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).tier || 'None'}</span>
                            </div>
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Major Threshold:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).baseThresholds.major || 'None'}</span>
                            </div>
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Severe Threshold:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).baseThresholds.severe || 'None'}</span>
                            </div>
                            <div className="inventory__equipment-property">
                              <span className="inventory__equipment-property-label">Base Armor Score:</span>
                              <span className="inventory__equipment-property-value">{(resolved as any).baseArmorScore || 'None'}</span>
                            </div>
                          </>
                        )}
                      </div>
                      
                      {/* Feature section - important for equipment special abilities */}
                      {(resolved as any).feature && (
                        <div className="inventory__equipment-description">
                          <span className="inventory__equipment-description-label">Feature:</span>
                          <p className="inventory__equipment-description-text">{(resolved as any).feature}</p>
                        </div>
                      )}
                      
                      {/* Quantity and Equipment Management */}
                      <motion.div 
                        className="inventory__equipment-actions" 
                        onClick={(e) => e.stopPropagation()}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        <motion.div 
                          className="inventory__equipment-controls"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                          >
                            <Input
                              value={ref.quantity.toString()}
                              onChange={(value) => handleReferenceQuantityChange(ref.itemId, parseInt(value) || 1)}
                              type="NUMBER"
                              placeholder="1"
                              label="Quantity"
                            />
                          </motion.div>
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.15 }}
                          >
                            <Button
                              variant={ref.equippedCount > 0 ? 'CLEAR' : 'SECONDARY'}
                              size="SMALL"
                              onClick={() => handleReferenceEquipToggle(ref.itemId, ref.equippedCount, ref.quantity)}
                            >
                              {ref.equippedCount > 0 ? 'Unequip' : 'Equip'}
                            </Button>
                          </motion.div>
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.15 }}
                          >
                            <Button
                              variant="CLEAR"
                              size="SMALL"
                              onClick={() => handleRemoveReference(ref.itemId)}
                            >
                              Remove
                            </Button>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loot Items Section */}
      <AnimatePresence>
        {lootReferences.length > 0 && (
          <motion.div
            key="loot-section"
            className="inventory__section"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ delay: 0.15, duration: 0.3 }}
          >
            <motion.h3
              className="inventory__section-title"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
            >
              Loot Items
            </motion.h3>
            <motion.div
              className="inventory__equipment-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <AnimatePresence>
                {lootReferences.map((ref) => {
                  const resolved = resolvedLoot.get(ref.itemId);
                  const isExpanded = expandedReferences.has(ref.itemId);
                  const isLoading = loadingLoot.has(ref.itemId);

                  return (
                    <motion.div
                      key={ref.itemId}
                      className="inventory__equipment-item"
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -20, scale: 0.95 }}
                      transition={{ delay: 0.05, duration: 0.2 }}
                    >
                      <motion.div
                        className="inventory__equipment-header"
                        onClick={() => toggleReferenceExpanded(ref.itemId)}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="inventory__equipment-title-row">
                          <motion.button
                            className={`inventory__dropdown-arrow ${isExpanded ? 'inventory__dropdown-arrow--expanded' : ''}`}
                            animate={{ rotate: isExpanded ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            ▼
                          </motion.button>
                          <motion.span className="inventory__equipment-name">
                            {resolved
                              ? resolved.name.charAt(0).toUpperCase() + resolved.name.slice(1)
                              : (isLoading ? 'Loading...' : ref.itemId.charAt(0).toUpperCase() + ref.itemId.slice(1))}
                          </motion.span>
                          <div className="inventory__equipment-quantities">
                            <span className="inventory__equipment-quantity">
                              ×{ref.quantity}
                            </span>
                            {(() => {
                              const equipmentRef = equipmentReferences.find(eqRef => eqRef.itemId === ref.itemId);
                              const equippedCount = equipmentRef?.equippedCount || 0;
                              return equippedCount > 0 ? (
                                <span className="inventory__equipment-equipped">
                                  ✓ {equippedCount} equipped
                                </span>
                              ) : null;
                            })()}
                          </div>
                        </div>
                      </motion.div>
                      <AnimatePresence>
                        {isExpanded && resolved && (
                          <motion.div
                            className="inventory__equipment-details"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.35 }}
                          >
                            <div className="inventory__equipment-properties">
                              {resolved.description && (
                                <div className="inventory__equipment-property">
                                  <span className="inventory__equipment-property-label">Description:</span>
                                  <span className="inventory__equipment-property-value">{resolved.description}</span>
                                </div>
                              )}
                            </div>
                            <motion.div
                              className="inventory__equipment-actions"
                              onClick={(e) => e.stopPropagation()}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.1 }}
                            >
                              <motion.div
                                className="inventory__equipment-controls"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                              >
                                <motion.div
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.2 }}
                                >
                                  <Input
                                    value={(ref.quantity ?? 1).toString()}
                                    onChange={(value) => handleLootQuantityChange(ref.itemId, parseInt(value) || 1)}
                                    type="NUMBER"
                                    placeholder="1"
                                    label="Quantity"
                                  />
                                </motion.div>
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ duration: 0.15 }}
                                  whileHover={{ scale: 1.05}}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <Button
                                    variant={(() => {
                                      const equipmentRef = equipmentReferences.find(eqRef => eqRef.itemId === ref.itemId);
                                      return (equipmentRef?.equippedCount || 0) > 0 ? 'CLEAR' : 'SECONDARY';
                                    })()}
                                    size="SMALL"
                                    onClick={() => handleLootEquipToggle(ref.itemId)}
                                  >
                                    {(() => {
                                      const equipmentRef = equipmentReferences.find(eqRef => eqRef.itemId === ref.itemId);
                                      return (equipmentRef?.equippedCount || 0) > 0 ? 'Unequip' : 'Equip';
                                    })()}
                                  </Button>
                                </motion.div>
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ duration: 0.15 }}
                                  whileHover={{ scale: 1.05}}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <Button
                                    variant="CLEAR"
                                    size="SMALL"
                                    onClick={() => handleRemoveLoot(ref.itemId)}
                                  >
                                    Remove
                                  </Button>
                                </motion.div>
                              </motion.div>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Consumables Section */}
      <AnimatePresence>
        {consumableReferences.length > 0 && (
          <motion.div
            key="consumables-section"
            className="inventory__section"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ delay: 0.175, duration: 0.3 }}
          >
            <motion.h3
              className="inventory__section-title"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.175 }}
            >
              Consumables
            </motion.h3>
            <motion.div
              className="inventory__equipment-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <AnimatePresence>
                {consumableReferences.map((ref) => {
                  const resolved = resolvedConsumables.get(ref.itemId);
                  const isExpanded = expandedReferences.has(ref.itemId);
                  const isLoading = loadingConsumables.has(ref.itemId);

                  return (
                    <motion.div
                      key={ref.itemId}
                      className="inventory__equipment-item"
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -20, scale: 0.95 }}
                      transition={{ delay: 0.05, duration: 0.2 }}
                    >
                      <motion.div
                        className="inventory__equipment-header"
                        onClick={() => toggleReferenceExpanded(ref.itemId)}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="inventory__equipment-title-row">
                          <motion.button
                            className={`inventory__dropdown-arrow ${isExpanded ? 'inventory__dropdown-arrow--expanded' : ''}`}
                            animate={{ rotate: isExpanded ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            ▼
                          </motion.button>
                          <motion.span className="inventory__equipment-name">
                            {resolved
                              ? resolved.name.charAt(0).toUpperCase() + resolved.name.slice(1)
                              : (isLoading ? 'Loading...' : ref.itemId.charAt(0).toUpperCase() + ref.itemId.slice(1))}
                          </motion.span>
                          <span
                            className="inventory__equipment-quantity"
                            style={{ display: 'flex', alignItems: 'center', gap: 'var(--semantic-spacing-component-xs)' }}
                          >
                            ×{ref.quantity}
                            {ref.quantity > MAX_CONSUMABLE_HOLD && (
                              <span
                                style={{ fontSize: 'var(--semantic-typography-body-small-font-size)', opacity: 0.6, cursor: 'help' }}
                                title="Official rules allow a maximum of 5 of each consumable"
                              >
                                ℹ️
                              </span>
                            )}
                          </span>
                        </div>
                      </motion.div>
                      <AnimatePresence>
                        {isExpanded && resolved && (
                          <motion.div
                            className="inventory__equipment-details"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.35 }}
                          >
                            <div className="inventory__equipment-properties">
                              {resolved.description && (
                                <div className="inventory__equipment-property">
                                  <span className="inventory__equipment-property-label">Description:</span>
                                  <span className="inventory__equipment-property-value">{resolved.description}</span>
                                </div>
                              )}
                            </div>
                            <motion.div
                              className="inventory__equipment-actions"
                              onClick={(e) => e.stopPropagation()}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.1 }}
                            >
                              <motion.div
                                className="inventory__equipment-controls"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                              >
                                <motion.div
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.2 }}
                                >
                                  <Input
                                    value={(ref.quantity ?? 1).toString()}
                                    onChange={(value) => handleConsumableQuantityChange(ref.itemId, parseInt(value) || 1)}
                                    type="NUMBER"
                                    placeholder="1"
                                    label="Quantity"
                                  />
                                </motion.div>
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ duration: 0.15 }}
                                  whileHover={{ scale: 1.05}}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <Button
                                    variant="SECONDARY"
                                    size="SMALL"
                                    onClick={() => handleConsume(ref.itemId)}
                                  >
                                    Consume
                                  </Button>
                                </motion.div>
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ duration: 0.15 }}
                                  whileHover={{ scale: 1.05}}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <Button
                                    variant="CLEAR"
                                    size="SMALL"
                                    onClick={() => handleRemoveConsumable(ref.itemId)}
                                  >
                                    Remove
                                  </Button>
                                </motion.div>
                              </motion.div>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual Inventory Section */}
      <motion.div
        className="inventory__section"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        <motion.h3
          className="inventory__section-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
        >
          Other Items
        </motion.h3>
        <motion.div
          className="inventory__equipment-list"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <AnimatePresence>
            {inventory.map((item, index) => {
              const isExpanded = expandedItems.has(index);
              return (
                <motion.div
                  key={index}
                  className="inventory__equipment-item"
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  transition={{ delay: 0.05, duration: 0.2 }}
                >
                  <motion.div
                    className="inventory__equipment-header"
                    onClick={() => toggleItemExpanded(index)}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="inventory__equipment-title-row">
                      <motion.button
                        className={`inventory__dropdown-arrow ${isExpanded ? 'inventory__dropdown-arrow--expanded' : ''}`}
                        animate={{ rotate: isExpanded ? 90 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        ▼
                      </motion.button>
                      <span className="inventory__equipment-name">
                        {item.name || `Item ${index + 1}`}
                      </span>
                      <AnimatePresence>
                        {item.quantity > 1 && (
                          <motion.span
                            className="inventory__equipment-quantity"
                            key={item.quantity}
                            initial={{ scale: 1.2, opacity: 0.7 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.2 }}
                          >
                            x{item.quantity}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      className="inventory__equipment-details"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.35 }}
                    >
                      <motion.div
                        className="inventory__equipment-actions"
                        onClick={(e) => e.stopPropagation()}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        <motion.div
                          className="inventory__equipment-controls"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                          >
                            <Input
                              value={item.name}
                              onChange={handleItemChange(index, 'name')}
                              placeholder="Item name"
                              label="Name"
                            />
                          </motion.div>
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.25 }}
                          >
                            <Input
                              value={item.quantity.toString()}
                              onChange={handleItemChange(index, 'quantity')}
                              type="NUMBER"
                              placeholder="1"
                              label="Qty"
                            />
                          </motion.div>
                          <motion.div
                            onClick={(e) => e.stopPropagation()}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3 }}
                          >
                            <Button
                              variant="CLEAR"
                              size="SMALL"
                              onClick={() => removeItem(index)}
                            >
                              Remove
                            </Button>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Input
                          value={item.description}
                          onChange={handleItemChange(index, 'description')}
                          placeholder="Item description"
                          label="Description"
                        />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
          <motion.div
            className="inventory__add-item"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
            >
              <Button
                variant="SECONDARY"
                size="SMALL"
                onClick={addItem}
              >
                + Add Item
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Empty State */}
      <AnimatePresence>
        {equipmentReferences.length === 0 && lootReferences.length === 0 && consumableReferences.length === 0 && inventory.length === 0 && (
          <motion.div 
            key="empty-state"
            className="inventory__empty"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.1, duration: 0.2 }}
          >
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              No items in inventory.
            </motion.p>
            <motion.small
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
            >
              Use the Content Library to add equipment or manually add items below.
            </motion.small>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Inventory;